import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, TextField, Button, Spinner, Alert, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type ProductListItem } from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import { DepartmentChip, FlagChip } from '../components/ProductChips';
import { Pill } from '../components/Pill';
import { primaryFlag } from '../lib/productChips';

/**
 * Product editor — list / search (Figma "Product editor list"). Browse the
 * canonical catalog: Product · Brand · GLPC · Department › Category · Flags.
 * "Edit" opens the detail. No UPC anywhere (feedback_never_show_upc); search by
 * a typed code is allowed as input.
 */

const GRID = '1.7fr 1fr 1.1fr 1.4fr 0.8fr 0.6fr';

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

export default function ProductEditor() {
  const caps = useCapabilities();
  const canView = caps.has('product:read');
  const canEdit = caps.has('product:edit');
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  // The recall-stub bin (migration 0152). recallSync MUST mint a product row
  // for every recalled item — tblactiverecalls is keyed on glpproductid, so no
  // row means no inventory alert — but nothing sells those rows. This is the
  // worklist for completing them by hand.
  const [binOnly, setBinOnly] = useState(false);
  const [worklist, setWorklist] = useState<'all' | 'incomplete' | 'exhausted'>('incomplete');
  const [nextId, setNextId] = useState<string | null>(null);
  const request = useRef(0);
  const [items, setItems] = useState<ProductListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (q: string, bin: boolean, list: typeof worklist, afterId?: string) => {
    const sequence = ++request.current;
    setLoading(true);
    setError(null);
    if (!afterId) { setItems([]); setNextId(null); }
    try {
      const res = await adminApiClient.listProducts({
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(bin ? { origin: 'food_recall' } : {}),
        worklist: list,
        ...(afterId ? { afterId } : {}),
        limit: 50,
      });
      if (sequence !== request.current) return;
      setItems(previous => afterId ? [...previous, ...res.items.filter(p => !previous.some(old => old.glpProductId === p.glpProductId))] : res.items);
      setNextId(res.nextId);
    } catch (err) {
      if (sequence === request.current) setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      if (sequence === request.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) void load(query, binOnly, worklist);
  }, [canView, query, binOnly, worklist, load]);

  if (caps.loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canView) return <Alert severity="error">You don’t have permission to view products.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Product editor
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Browse and edit the canonical product catalog — fix names, sizes, classification, and review
        duplicate variants.
      </Typography>

      {!canEdit && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search products by name, brand, UPC, or GLPC"
          ariaLabel="Search products"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 280 }}
        />
        <Button ariaLabel="Search" buttonText="Search" variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={() => setQuery(search)} />
        <Button
          ariaLabel={binOnly ? 'Show all products' : 'Show only recall-stub products needing completion'}
          buttonText={binOnly ? 'Recall bin ✓' : 'Recall bin'}
          variant={binOnly ? 'contained' : 'outlined'}
          size="small"
          sx={binOnly ? undefined : NEUTRAL_BTN}
          onClick={() => setBinOnly((v) => !v)}
        />
        <Button ariaLabel="Variant dedup review" buttonText="Variant dedup ›" variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={() => navigate('/product-editor/variants')} />
        {caps.data?.features?.productEvidenceReview&&<Button ariaLabel="Product evidence review" buttonText="Evidence review" variant="outlined" size="small" onClick={()=>navigate('/product-evidence')}/>}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {(['all', 'incomplete', 'exhausted'] as const).map(value => <Button key={value} ariaLabel={`${value} products`} buttonText={value === 'all' ? 'All products' : value === 'incomplete' ? 'Incomplete products' : 'Sources exhausted'} variant={worklist === value ? 'contained' : 'outlined'} size="small" onClick={() => setWorklist(value)} />)}
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>Incomplete products stay here even after a review. Sources exhausted means automated lookups stopped; it does not mean the product is complete.</Typography>
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading && items.length === 0 ? (
        <Centered>
          <Spinner color="brand" />
        </Centered>
      ) : error && items.length === 0 ? null : items.length === 0 ? (
        <EmptyState
          icon="listAlt"
          title={binOnly ? 'Recall bin is empty' : 'No products'}
          body={
            binOnly
              ? 'Every recall-born row has been completed or matched to a real retailer. Rows leave this bin automatically once a store is found to carry them.'
              : 'Search the catalog by name, brand, UPC, or GLPC.'
          }
        />
      ) : (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
          <Box
            sx={{
              display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1.25,
              backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0',
            }}
          >
            {['Product', 'Brand', 'GLPC', 'Department / Category', 'Flags', ''].map((h, i) => (
              <Typography key={i} variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>
                {h.toUpperCase()}
              </Typography>
            ))}
          </Box>
          {items.map((p, idx) => (
            <Box
              key={p.glpProductId}
              sx={{
                display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1.5, alignItems: 'center',
                borderBottom: idx < items.length - 1 ? '1px solid #eef2f6' : 'none',
                '&:hover': { backgroundColor: '#fafbfc' },
              }}
            >
              <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{p.itemName}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{p.brand || '—'}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '12px' }}>
                {p.glpProductId}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                <DepartmentChip department={p.glpDepartment} />
                <Typography variant="caption" sx={{ color: '#6b7280' }}>{p.glpCategory || '—'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                <FlagChip flag={primaryFlag(p)} />
                <Typography variant="caption">{p.missingFields?.length ? `Needs: ${p.missingFields.join(', ')}` : p.hasAllProductData ? 'Complete' : 'Review completion status'}</Typography>
                {p.enrichmentExhaustedAt && <Typography variant="caption">Sources exhausted</Typography>}
                {p.reviewCompleted && <Typography variant="caption">Reviewed</Typography>}
                {p.originSource === 'food_recall' && (
                  // Without this badge a recall stub reads as a corrupted
                  // product: the recalling FIRM sits in "brand", the name is
                  // ALL-CAPS regulatory text cut at "NET WT", and there is no
                  // price or image. Naming the origin makes it legible.
                  <Pill label="Recall stub" fg="#b9700f" bg="#fdf2e3" />
                )}
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Button
                  ariaLabel={`Edit ${p.itemName}`}
                  buttonText="Edit"
                  variant="outlined"
                  size="small"
                  sx={NEUTRAL_BTN}
                  onClick={() => navigate(`/product-editor/${encodeURIComponent(p.glpProductId)}`)}
                />
              </Box>
            </Box>
          ))}
          <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', mr: 2 }}>Showing {items.length}</Typography>
            {nextId && <Button variant="outlined" ariaLabel="Load more products" buttonText={loading ? 'Loading…' : 'Load more'} size="small" disabled={loading} onClick={() => void load(query, binOnly, worklist, nextId)} />}
          </Box>
        </Box>
      )}
    </Box>
  );
}
